<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { ElTour, ElTourStep } from 'element-plus'
import { ArrowRight, Check, Clipboard, ImagePlus, MousePointer2, Plus, Search, Trash2, Volume2 } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { BUFF_TOUR_STORAGE_KEY, buffTourHistory } from '@/buffs/tour-history'
import { buffTourCopy } from '@/i18n/buff-tour'
import { normalizeLocale } from '@/i18n/locales'
import { PREVIEW_BUFF_ICONS } from '@/buffs/preview-sample'

const props = defineProps<{ ready: boolean; blocked: boolean }>()
const emit = defineEmits<{ 'visibility-change': [visible: boolean] }>()
const route = useRoute()
const { t, locale } = useI18n()
const copy = computed(() => buffTourCopy[normalizeLocale(locale.value)])
const open = ref(false)
const seen = ref(buffTourHistory.hasSeen())
const pageVisible = ref(document.visibilityState === 'visible')
const eligible = computed(() => route.name === 'live' && props.ready && !props.blocked && pageVisible.value && !seen.value)
let observer: MutationObserver | undefined
let checking = false

const bar = () => document.querySelector<HTMLElement>('.character-buffs .buff-items')
const add = () => document.querySelector<HTMLElement>('.character-buffs .buff-add')
const settings = () => document.querySelector<HTMLElement>('.character-buffs .buff-item:not(.buff-add)') ?? add()

async function check(): Promise<void> {
  if (checking || open.value || !eligible.value) return
  checking = true
  try {
    await nextTick()
    if (!eligible.value || !bar() || !add()) return
    open.value = true
    emit('visibility-change', true)
  } finally { checking = false }
}

function dismiss(): void {
  if (!open.value) return
  buffTourHistory.markSeen()
  seen.value = true
  open.value = false
  emit('visibility-change', false)
}

function visibilityChanged(): void { pageVisible.value = document.visibilityState === 'visible' }
function storageChanged(event: StorageEvent): void {
  if (event.key === BUFF_TOUR_STORAGE_KEY || event.key === null) {
    seen.value = buffTourHistory.hasSeen()
    if (seen.value) dismiss()
  }
}

watch(eligible, allowed => { if (allowed) void check(); else dismiss() }, { immediate: true, flush: 'post' })
onMounted(() => {
  observer = new MutationObserver(() => { if (eligible.value) void check() })
  observer.observe(document.querySelector('#main-content') ?? document.body, { childList: true, subtree: true })
  document.addEventListener('visibilitychange', visibilityChanged)
  window.addEventListener('storage', storageChanged)
  void check()
})
onBeforeUnmount(() => {
  observer?.disconnect()
  document.removeEventListener('visibilitychange', visibilityChanged)
  window.removeEventListener('storage', storageChanged)
  dismiss()
})
</script>

<template>
  <ElTour class="buff-tour" :model-value="open" :mask="true" :gap="{ offset: 4, radius: 6 }" :target-area-clickable="false" :scroll-into-view-options="{ block: 'center', behavior: 'instant' }"
    :content-style="{ width: 'min(24rem, calc(100vw - 2rem))', maxWidth: 'min(24rem, calc(100vw - 2rem))' }" @update:model-value="value => { if (!value) dismiss() }" @close="dismiss" @finish="dismiss">
    <template #indicators="{ current, total }"><span class="buff-tour-progress">{{ current + 1 }} / {{ total }}</span></template>
    <ElTourStep :target="bar" :title="copy.barTitle">
      <p class="buff-tour-copy"><strong>{{ copy.barLead }}</strong>{{ copy.barDescription }}</p>
      <figure class="buff-tour-visual" aria-hidden="true">
        <figcaption>{{ copy.example }}<span class="buff-tour-live"><i />{{ t('buffs.control.running') }}</span></figcaption>
        <div class="buff-tour-rail">
          <span class="buff-tour-icon"><img :src="PREVIEW_BUFF_ICONS.bless" alt="" width="40" height="40"><span class="buff-tour-seconds">28</span></span>
          <span class="buff-tour-icon is-near"><img :src="PREVIEW_BUFF_ICONS.sharpEyes" alt="" width="40" height="40"><span class="buff-tour-seconds">8</span><Volume2 class="buff-tour-voice" :size="16" /></span>
          <span class="buff-tour-icon"><img :src="PREVIEW_BUFF_ICONS.mapleWarrior" alt="" width="40" height="40"></span>
          <span class="buff-tour-plus"><Plus :size="20" /></span>
        </div>
        <div class="buff-tour-callout"><Volume2 :size="15" /><span>{{ t('buffs.nearingNotice', { name: '' }) }}</span></div>
      </figure>
      <p class="buff-tour-hint">{{ copy.barHint }}</p>
    </ElTourStep>
    <ElTourStep :target="add" :title="copy.addTitle">
      <p class="buff-tour-copy"><strong>{{ copy.addLead }}</strong>{{ copy.addDescription }}</p>
      <figure class="buff-tour-visual" aria-hidden="true">
        <figcaption>{{ copy.example }}</figcaption>
        <div class="buff-tour-add-flow">
          <span class="buff-tour-plus is-emphasized"><Plus :size="22" /></span><ArrowRight class="buff-tour-arrow" :size="18" />
          <div class="buff-tour-catalog">
            <div class="buff-tour-tabs"><strong>{{ t('buffs.skills') }}</strong><span>{{ t('buffs.items') }}</span></div>
            <div class="buff-tour-search"><Search :size="14" /><span>{{ t('buffs.online') }}</span></div>
            <div class="buff-tour-catalog-icons"><img :src="PREVIEW_BUFF_ICONS.bless" alt="" width="28" height="28"><span class="buff-tour-selected"><img :src="PREVIEW_BUFF_ICONS.sharpEyes" alt="" width="28" height="28"><Check :size="12" /></span><img :src="PREVIEW_BUFF_ICONS.mapleWarrior" alt="" width="28" height="28"></div>
          </div>
        </div>
        <div class="buff-tour-import"><span><ImagePlus :size="14" />{{ t('buffs.upload') }}</span><span><Clipboard :size="14" />{{ t('buffs.paste') }}</span></div>
      </figure>
      <p class="buff-tour-hint">{{ copy.addHint }}</p>
    </ElTourStep>
    <ElTourStep :target="settings" :title="copy.settingsTitle">
      <p class="buff-tour-copy"><strong>{{ copy.settingsLead }}</strong>{{ copy.settingsDescription }}</p>
      <figure class="buff-tour-visual" aria-hidden="true">
        <figcaption>{{ copy.example }}</figcaption>
        <div class="buff-tour-settings-flow">
          <span class="buff-tour-icon buff-tour-click"><img :src="PREVIEW_BUFF_ICONS.sharpEyes" alt="" width="40" height="40"><MousePointer2 :size="22" /></span><ArrowRight class="buff-tour-arrow" :size="18" />
          <div class="buff-tour-settings">
            <div class="buff-tour-setting"><span>{{ t('buffs.itemSettings.nearing') }}</span><strong class="buff-tour-threshold">10 <small>{{ t('buffs.itemSettings.seconds') }}</small></strong></div>
            <div class="buff-tour-setting"><span>{{ t('buffs.itemSettings.speech') }}</span><span class="buff-tour-toggle" /></div>
            <div class="buff-tour-settings-footer"><span class="buff-tour-delete"><Trash2 :size="13" />{{ t('buffs.itemSettings.delete') }}</span></div>
          </div>
        </div>
      </figure>
      <p class="buff-tour-hint">{{ copy.settingsHint }}</p>
    </ElTourStep>
  </ElTour>
</template>

<style>
.buff-tour .el-tour__content {
  display: flex; flex-direction: column; max-height: calc(100dvh - 2rem); padding: 1rem;
  border: 1px solid var(--border); border-radius: .875rem; background: var(--surface); color: var(--text-strong);
}
.buff-tour .el-tour__header, .buff-tour .el-tour__footer { flex: none; }
.buff-tour .el-tour__title { font-size: 1rem; line-height: 1.45; font-weight: 700; color: var(--text-strong); text-wrap: balance; }
.buff-tour .el-tour__body { min-height: 0; overflow-y: auto; overscroll-behavior: contain; }
.buff-tour .el-tour__arrow { background: var(--surface); }
.buff-tour .el-tour__footer { align-items: center; }
.buff-tour-progress { color: var(--muted-strong); font-size: .75rem; font-variant-numeric: tabular-nums; }
.buff-tour-copy { margin: 0 0 .875rem; font-size: .8125rem; line-height: 1.65; color: var(--text); }
.buff-tour-copy strong { font-weight: 650; color: var(--text-strong); }
.buff-tour-hint { margin: .75rem 0 0; font-size: .75rem; line-height: 1.6; color: var(--muted-strong); }
.buff-tour-visual { margin: 0; padding: .75rem; border: 1px solid var(--border); border-radius: .625rem; background: var(--surface-subtle); user-select: none; }
.buff-tour-visual figcaption { display: flex; align-items: center; justify-content: space-between; gap: .5rem; margin-bottom: .875rem; color: var(--muted-strong); font-size: .6875rem; line-height: 1.4; }
.buff-tour-live { display: inline-flex; align-items: center; gap: .3125rem; }
.buff-tour-live i { width: .375rem; height: .375rem; border-radius: 50%; background: var(--green); }
.buff-tour-rail { display: flex; justify-content: center; align-items: center; gap: .875rem; padding: .625rem .25rem; border: 1px solid var(--border); border-radius: .5rem; background: var(--surface); }
.buff-tour-icon { position: relative; display: inline-flex; width: 2.5rem; height: 2.5rem; flex: none; }
.buff-tour-icon img { width: 100%; height: 100%; border-radius: .3125rem; object-fit: contain; }
.buff-tour-icon.is-near { outline: 2px solid var(--amber); outline-offset: 3px; border-radius: .3125rem; }
.buff-tour-seconds { position: absolute; bottom: 0; left: 50%; transform: translateX(-50%); min-width: 1.375rem; padding: 0 .1875rem; border-radius: .1875rem; background: #19232e; color: #fff; text-align: center; font: 700 .6875rem/1.125rem ui-monospace, monospace; }
.is-near .buff-tour-seconds { background: var(--amber-soft); color: var(--amber); }
.buff-tour-voice { position: absolute; top: -.5rem; right: -.625rem; padding: .125rem; box-sizing: content-box; border: 1px solid var(--border); border-radius: 50%; background: var(--surface); color: var(--amber); }
.buff-tour-plus { display: inline-flex; align-items: center; justify-content: center; width: 2.5rem; height: 2.5rem; box-sizing: border-box; flex: none; border: 1px dashed var(--border-strong); border-radius: .4375rem; color: var(--muted-strong); }
.buff-tour-plus.is-emphasized { background: var(--primary-soft); border-style: solid; border-color: color-mix(in srgb, var(--primary) 35%, var(--border)); color: var(--primary); }
.buff-tour-callout { display: flex; justify-content: center; align-items: center; gap: .375rem; margin-top: .75rem; font-size: .75rem; color: var(--amber); }
.buff-tour-add-flow, .buff-tour-settings-flow { display: flex; align-items: center; justify-content: center; gap: .625rem; }
.buff-tour-arrow { flex: none; color: var(--muted); }
.buff-tour-catalog, .buff-tour-settings { min-width: 0; flex: 1; padding: .625rem; border: 1px solid var(--border); border-radius: .5rem; background: var(--surface); }
.buff-tour-tabs { display: flex; gap: .75rem; font-size: .6875rem; color: var(--muted-strong); }
.buff-tour-tabs strong { color: var(--primary); }
.buff-tour-search { display: flex; align-items: center; gap: .375rem; margin: .5rem 0; padding: .3125rem .375rem; border: 1px solid var(--border); border-radius: .3125rem; color: var(--muted-strong); font-size: .6875rem; }
.buff-tour-search svg, .buff-tour-import svg, .buff-tour-delete svg { flex: none; }
.buff-tour-catalog-icons { display: flex; align-items: center; justify-content: space-around; gap: .5rem; }
.buff-tour-catalog-icons img { display: block; width: 1.75rem; height: 1.75rem; object-fit: contain; border-radius: .1875rem; }
.buff-tour-selected { position: relative; }
.buff-tour-selected svg { position: absolute; right: -.25rem; bottom: -.125rem; border-radius: 50%; padding: .0625rem; box-sizing: content-box; background: var(--green); color: var(--surface); }
.buff-tour-import { display: flex; justify-content: center; flex-wrap: wrap; gap: .5rem 1rem; margin-top: .75rem; font-size: .6875rem; color: var(--muted-strong); }
.buff-tour-import > span { display: inline-flex; align-items: center; gap: .3125rem; }
.buff-tour-click > svg { position: absolute; right: -.4375rem; bottom: -.5rem; color: var(--primary); fill: var(--surface); }
.buff-tour-setting { display: flex; align-items: center; justify-content: space-between; gap: .5rem; padding: .4375rem 0; font-size: .6875rem; line-height: 1.4; }
.buff-tour-setting + .buff-tour-setting { border-top: 1px solid var(--border); }
.buff-tour-threshold { flex: none; padding: .1875rem .375rem; border-radius: .25rem; background: var(--primary-soft); color: var(--primary); font-size: .8125rem; }
.buff-tour-threshold small { font-size: .625rem; font-weight: 500; }
.buff-tour-toggle { display: inline-flex; align-items: center; justify-content: flex-end; flex: none; width: 1.5rem; height: .875rem; padding: .125rem; box-sizing: border-box; border-radius: 1rem; background: var(--primary); }
.buff-tour-toggle::after { content: ''; width: .625rem; height: .625rem; border-radius: 50%; background: #fff; }
.buff-tour-settings-footer { margin-top: .25rem; padding-top: .5rem; border-top: 1px solid var(--border); }
.buff-tour-delete { display: inline-flex; align-items: center; gap: .25rem; padding: .3125rem .4375rem; border: 1px solid color-mix(in srgb, var(--danger) 25%, var(--border)); border-radius: .375rem; background: color-mix(in srgb, var(--danger) 6%, var(--surface)); color: var(--danger); font-size: .6875rem; line-height: 1.4; }
.buff-tour .el-tour-buttons { display: flex; align-items: center; gap: .625rem; }
.buff-tour .el-tour-buttons .el-button {
  min-height: 2rem;
  height: 2rem;
  padding-inline: .75rem;
  border-radius: .5rem;
  font-size: .8125rem;
}
@media (max-width: 415px) {
  .buff-tour .el-tour__content { left: 1rem !important; }
  .buff-tour .el-tour__arrow { display: none; }
}
</style>
